import { useState } from "react";
import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Star, ArrowRight, ExternalLink, Quote } from "lucide-react";
import { REFERENCES, REVIEW_QUOTES } from "@/lib/referencesData";
import { SITE } from "@/lib/siteData";
import { ROUTE_SEO } from "@/lib/routeSeo";

export default function References() {
  const [openId, setOpenId] = useState(null);
  const open = REFERENCES.find(r => r.id === openId);

  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/references"].title}
        description={ROUTE_SEO["/references"].meta}
        path="/references"
        jsonLd={breadcrumbSchema([{name: "Home", path: "/"}, {name: "References"}])}
      />
      <PageHero
        kicker="Social proof"
        title={ROUTE_SEO["/references"].h1}
        subtitle="Reference letters, testimonials and Google reviews from the brands and institutions we've worked with - verified, on letterhead, with names."
        breadcrumb={[{name: "Home", path: "/"}, {name: "References"}]}
      />

      {/* Reference letters grid */}
      <section className="section-pad">
        <div className="container-site">
          <p className="kicker mb-3">Reference letters</p>
          <h2 className="font-display uppercase text-foreground text-3xl sm:text-4xl mb-10">On letterhead. Click to read.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REFERENCES.map(r => (
              <article key={r.id} className="group rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
                <button data-testid={`reference-preview-${r.id}`} onClick={() => setOpenId(r.id)} className="relative block aspect-[3/4] bg-[hsl(225,22%,4%)] overflow-hidden">
                  <picture>
                    <source srcSet={r.imageWebp} type="image/webp" />
                    <img src={r.thumb} alt={`Reference letter from ${r.client}`} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]" />
                  </picture>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">{r.sector} · {r.year}</div>
                    <div className="mt-1 font-display text-xl uppercase tracking-[0.02em] text-white leading-tight">{r.client}</div>
                  </div>
                  <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/60 border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-white backdrop-blur-sm">Read letter <ExternalLink className="h-3 w-3" /></span>
                </button>
                <div className="p-6 flex-1 flex flex-col">
                  <Quote className="h-5 w-5 text-[hsl(var(--primary))] mb-3" />
                  <blockquote className="text-sm sm:text-base text-foreground/90 leading-relaxed flex-1">"{r.quote}"</blockquote>
                  <div className="mt-5 pt-5 border-t border-border">
                    <div className="font-display text-base uppercase tracking-[0.02em] text-foreground">{r.client}</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{r.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Google review carousel */}
      <section className="section-pad bg-[hsl(225,22%,4%)] border-y border-border">
        <div className="container-site">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="kicker mb-3">Google reviews</p>
              <h2 className="font-display uppercase text-foreground text-3xl sm:text-4xl">Verified client reviews.</h2>
            </div>
            <Button asChild variant="ghost" className="h-11 rounded-full border border-border bg-card/60 hover:bg-card px-5"><a href={SITE.gmb.searchUrl} target="_blank" rel="noreferrer noopener">View on Google <ExternalLink className="ml-2 h-3.5 w-3.5" /></a></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REVIEW_QUOTES.map((rev) => (
              <figure key={rev.name || rev.text} className="rounded-2xl border border-border bg-card p-6 flex flex-col">
                <div className="flex items-center gap-1 mb-3">{Array.from({length: rev.rating}).map((_,j) => <Star key={`${rev.name}-star-${j}`} className="h-4 w-4 text-[hsl(var(--primary))] fill-current" />)}</div>
                <blockquote className="text-sm sm:text-base text-foreground/90 leading-relaxed flex-1">"{rev.text}"</blockquote>
                <figcaption className="mt-5 pt-5 border-t border-border">
                  <div className="font-display text-base uppercase tracking-[0.02em] text-foreground">{rev.name}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{rev.role} · {rev.source}</div>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button asChild className="h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-5"><a href={SITE.gmb.profileUrl} target="_blank" rel="noreferrer noopener">Leave a review</a></Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container-site text-center">
          <h2 className="font-display uppercase text-foreground text-3xl sm:text-5xl text-balance">Add your name to the list.</h2>
          <p className="mt-4 max-w-xl mx-auto text-sm sm:text-base text-muted-foreground">Whether you're a Cape Town brand or an international team - let's make something we'll both want a reference letter for.</p>
          <div className="mt-8"><Button asChild className="h-12 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-6"><Link to="/contact">Start a project <ArrowRight className="ml-2 h-4 w-4" /></Link></Button></div>
        </div>
      </section>

      {/* Lightbox for full reference letter */}
      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent data-testid="reference-letter-dialog" className="max-w-3xl w-[94vw] p-0 bg-card border-border overflow-hidden max-h-[90vh] overflow-y-auto">
          <DialogTitle className="sr-only">{open?.client || "Reference letter"}</DialogTitle>
          {open && (<>
            <div className="px-6 py-5 border-b border-border">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">{open.sector} · {open.year}</div>
              <div className="mt-1 font-display text-2xl uppercase tracking-[0.02em] text-foreground">{open.client}</div>
            </div>
            <div className="bg-[hsl(225,22%,4%)] p-4">
              <picture>
                <source srcSet={open.imageWebp} type="image/webp" />
                <img src={open.image} alt={`Reference letter from ${open.client}`} className="w-full rounded-lg border border-border" />
              </picture>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </SiteShell>
  );
}
